import { getFormProps, useForm } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import { type GeneratedImage } from '@prisma/client'
import { Form, useFetcher } from '@remix-run/react'
import { blurhashToCssGradientString } from '@unpic/placeholder'
import { Image } from '@unpic/react'
import { useEffect, useState, type Dispatch, type SetStateAction } from 'react'
import { useSpinDelay } from 'spin-delay'
import { CharacterSliders } from '#app/routes/generate+/_layout'
import {
	DROP_SIZE_MAX,
	DROP_SIZE_MIN,
} from '#app/routes/resources+/face-detailer'
import { type GeneratedImage2 } from '#app/routes/resources+/generated-images'
import { IMAGE_BREAKPOINTS } from '#app/utils/constants'
import {
	type DBModel,
	type DBSampler,
	type DBScheduler,
	decodeImageFormat,
	defaultSampler,
	defaultScheduler,
	FaceDetailerSchema,
	getDisplayModel,
	getDisplaySampler,
	getDisplayScheduler,
	modelKeys,
	samplerKeys,
	schedulerKeys,
} from '#app/utils/helpers'
import { useUserCredits } from '#app/utils/hooks/useUserCredits'
import { cn } from '#app/utils/misc'
import { CustomImage } from './custom-image'
import { ErrorList } from './forms'
import { Button } from './ui/button'

import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from './ui/dialog'
import HiddenInput from './ui/hidden-input'
import { Icon } from './ui/icon'
import { Input } from './ui/input'
import { Label } from './ui/label'
import PromptField from './ui/prompt-field'
import { ScrollArea } from './ui/scroll-area'

import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from './ui/select'
import Spinner from './ui/spinner'

export function DialogueDetailer({
	open,
	onClose,
	image,
}: {
	open: boolean
	onClose: Dispatch<SetStateAction<boolean>>
	image: GeneratedImage2
}) {
	const [model, setModel] = useState<DBModel | undefined>(undefined)

	const imageFetcher = useFetcher()
	const [isLoading, setIsLoading] = useState(true)

	useEffect(() => {
		if (open && image.id && imageFetcher.state === 'idle') {
			setIsLoading(true)
			imageFetcher.load(`/resources/image?id=${image.id}`)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, image.id])

	useEffect(() => {
		if (imageFetcher.state === 'idle' && imageFetcher.data) {
			setIsLoading(false)
		}
	}, [imageFetcher.state, imageFetcher.data])

	const showSpinner = useSpinDelay(isLoading, {
		delay: 150,
		minDuration: 500,
	})

	const imageData = (imageFetcher.data as { image: GeneratedImage })?.image

	useEffect(() => {
		if (imageData?.model) {
			setModel(imageData?.model as DBModel)
		}
	}, [imageData?.model])

	const [form, fields] = useForm({
		id: 'face-detailer',
		constraint: getZodConstraint(FaceDetailerSchema),
		lastResult: null,
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: FaceDetailerSchema })
		},
		shouldRevalidate: 'onInput',
		defaultValue: {
			prompt: imageData?.positivePrompt,
			negativePrompt: imageData?.negativePrompt,
			model: model as DBModel,
			imageFormat: `1-1-512-512`,
			denoise: 0.35,
			batchSize: 1,
			steps: 20,
			cfg: 6,
			sampler: (imageData?.sampler || defaultSampler) as DBSampler,
			scheduler: (imageData?.scheduler || defaultScheduler) as DBScheduler,
			seed: -1,
			clipSkip: -2,
			dropSize: 10,
			bboxThreshold: 0.5,
			workflow: 'face-detailer' as const,
			inputImage: {
				id: image.id,
				ownerId: image.ownerId,
			},
		},
	})

	const [batchSize, setBatchSize] = useState<number | string>(
		Number(fields.batchSize.initialValue as string),
	)
	const [steps, setSteps] = useState<number | string>(
		Number(fields.steps.initialValue as string),
	)
	const [cfg, setCfg] = useState<number | string>(
		Number(fields.cfg.initialValue as string),
	)
	const [seed] = useState<number>(Number(fields.seed.initialValue as string))
	const [denoise, setDenoise] = useState<number | string>(
		Number(fields.denoise.initialValue as string),
	)

	const [sampler, setSampler] = useState<DBSampler>(
		(fields.sampler.initialValue as DBSampler) ||
			imageData?.sampler ||
			defaultSampler,
	)

	const [scheduler, setScheduler] = useState<DBScheduler>(
		(fields.scheduler.initialValue as DBScheduler) ||
			imageData?.scheduler ||
			defaultScheduler,
	)
	const [dropSize, setDropSize] = useState<number | string>(
		fields.dropSize.initialValue || 10,
	)
	const [bboxThreshold, setBboxThreshold] = useState<number | string>(
		fields.bboxThreshold.initialValue || 0.5,
	)

	useEffect(() => {
		if (fields.batchSize.initialValue) {
			setBatchSize(+fields.batchSize.initialValue)
		}
	}, [fields.batchSize.initialValue])

	useEffect(() => {
		if (fields.steps.initialValue) {
			setSteps(+fields.steps.initialValue)
		}
	}, [fields.steps.initialValue])

	useEffect(() => {
		if (fields.cfg.initialValue) {
			setCfg(+fields.cfg.initialValue)
		}
	}, [fields.cfg.initialValue])

	useEffect(() => {
		if (fields.sampler.initialValue) {
			setSampler(fields.sampler.initialValue as DBSampler)
		}
	}, [fields.sampler.initialValue])

	useEffect(() => {
		if (fields.scheduler.initialValue) {
			setScheduler(fields.scheduler.initialValue as DBScheduler)
		}
	}, [fields.scheduler.initialValue])

	useEffect(() => {
		if (fields.dropSize.initialValue) {
			setDropSize(fields.dropSize.initialValue)
		}
	}, [fields.dropSize.initialValue])

	useEffect(() => {
		if (fields.bboxThreshold.initialValue) {
			setBboxThreshold(fields.bboxThreshold.initialValue)
		}
	}, [fields.bboxThreshold.initialValue])

	const [generationCost, hasCredits] = useUserCredits(
		Number(batchSize),
		model as DBModel,
	)

	const renderedImageHeight = document.getElementById(
		'dialog-image-container',
	)?.offsetHeight

	return (
		<>
			<Dialog open={open} onOpenChange={onClose}>
				<DialogContent
					className="flex h-5/6 flex-col sm:max-w-[425px] md:max-w-[600px] lg:max-w-[800px]"
					onClick={(e) => e.stopPropagation()}
					forceMount
				>
					<DialogHeader>
						<DialogTitle>Face Detailer</DialogTitle>
						<DialogDescription>
							Refine facial features of your generated image.
						</DialogDescription>
					</DialogHeader>
					{showSpinner ? (
						<div className="flex-grow">
							<Spinner />
						</div>
					) : isLoading ? (
						<div className="flex-grow"></div>
					) : (
						<Form
							key={image.id}
							method="POST"
							action="/resources/face-detailer"
							{...getFormProps(form)}
							onSubmit={(e) => {
								if (!hasCredits) {
									e.preventDefault()
								} else {
									onClose(false)
								}
							}}
							className="flex flex-grow flex-col overflow-hidden"
						>
							<HiddenInput
								field={fields.inputImage}
								value={JSON.stringify({
									id: image.id,
									ownerId: image.ownerId,
								})}
							/>
							<HiddenInput
								field={fields.imageFormat}
								// value={imageData?.format}
								value="1-1-512-512"
							/>
							<HiddenInput field={fields.denoise} value={denoise.toString()} />
							<HiddenInput field={fields.model} value={model || ''} />
							<HiddenInput
								field={fields.batchSize}
								value={seed !== -1 ? 1 : batchSize}
							/>
							<HiddenInput field={fields.steps} value={steps} />
							<HiddenInput field={fields.cfg} value={cfg} />
							<HiddenInput field={fields.sampler} value={sampler} />
							<HiddenInput field={fields.scheduler} value={scheduler} />
							<HiddenInput field={fields.dropSize} value={dropSize} />
							<HiddenInput field={fields.bboxThreshold} value={bboxThreshold} />
							<HiddenInput field={fields.workflow} value="face-detailer" />
							<HiddenInput field={fields.clipSkip} value={-2} />
							<ScrollArea className="w-full flex-grow px-4">
								<div className="space-y-4">
									<div className="flex flex-col items-center justify-center gap-4 md:flex-row">
										<div className="w-full" id={image.id}>
											{/* we don't need the placeholder blurhash here */}
											<Image
												key={image.id}
												loading="eager"
												src={`https://myr2zone.b-cdn.net/${image.ownerId}/${image.id}.png`}
												layout="constrained"
												width={500}
												// height={computeHeightFromWidthAndAspectRatio(
												// 	decodeImageFormat(image.format),
												// 	500,
												// )}
												aspectRatio={
													decodeImageFormat(image.format).ratio.width /
													decodeImageFormat(image.format).ratio.height
												}
												cdn="bunny"
												className="rounded-lg border-1 border-gray-400 opacity-100"
												alt=""
												background={blurhashToCssGradientString(image.blurhash)}
												breakpoints={IMAGE_BREAKPOINTS}
											/>
										</div>
										<div
											className="flex h-full w-full flex-col items-start justify-start"
											style={{
												maxHeight: `${document.getElementById(image.id)?.offsetHeight || 0}px`,
											}}
										>
											<Label className="text-sm font-medium">
												<Icon name="palette" className="h-5 w-5">
													Choose face restoration model:
												</Icon>{' '}
											</Label>
											<ScrollArea
												style={{
													height: `${renderedImageHeight}px`,
												}}
												className="m-0 h-full w-full p-0 pr-4"
											>
												<div className="grid grid-cols-2 gap-4 p-1">
													{modelKeys
														.filter((key) => key.includes('flux'))
														.map((option) => (
															<div
																key={option}
																onClick={() => setModel(option)}
																className={cn(
																	'group flex flex-col items-center justify-center overflow-hidden rounded-lg transition-all duration-300 ease-in-out',
																	'hover:cursor-pointer hover:shadow-xl hover:ring-2 hover:ring-gray-700',
																	option === model && 'ring-2 ring-primary',
																)}
															>
																<div
																	className="relative h-full w-full"
																	id="dialog-image-container"
																>
																	<CustomImage
																		key={image.id}
																		image={image as GeneratedImage2}
																		width={250}
																		actualImageUrl="https://myr2zone.b-cdn.net/clqp2p0i3001vgqawmi582jkn/cbqlzb3vypadyi71g78g3r2t.png"
																		imageFormat={{
																			ratio: {
																				width: 1,
																				height: 1,
																			},
																			resolution: {
																				width: 250,
																				height: 250,
																			},
																		}}
																	/>
																	<div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
																	<div className="absolute inset-0 flex flex-col items-center justify-end p-2">
																		<h3 className="text-center text-xs font-bold text-white drop-shadow-lg">
																			{getDisplayModel(option)}
																		</h3>
																	</div>
																	{option === model && (
																		<div className="absolute right-1 top-1 z-20 flex items-center justify-center rounded-full bg-primary p-1 shadow-md ring-1 ring-white">
																			<Icon
																				name="check"
																				className="h-3 w-3 text-primary-foreground"
																				strokeWidth={3}
																			/>
																		</div>
																	)}
																</div>
																<div className="h-1 w-full scale-x-0 bg-primary transition-transform duration-300 ease-in-out group-hover:scale-x-100"></div>
															</div>
														))}
												</div>
											</ScrollArea>
										</div>
									</div>

									<div className="flex w-full flex-row items-center justify-center">
										<PromptField
											field={fields.prompt}
											icon={
												<Icon name="smiley" className="h-5 w-5 text-white" />
											}
											title="Positive prompt"
											enableScroll
											placeholder="Describe the facial features you'd like to see."
											textareaClassName="h-full w-full"
										/>

										<PromptField
											field={fields.negativePrompt}
											icon={
												<Icon name="prohibit" className="h-5 w-5 text-white" />
											}
											title="Negative prompt"
											enableScroll
											placeholder="Describe the facial features you'd like to avoid."
											textareaClassName="h-full w-full"
										/>
									</div>

									<div className="flex flex-row items-start justify-center">
										<CharacterSliders
											isEssentials={false}
											fields={fields}
											values={{
												steps,
												batchSize,
												cfg,
												seed,
												denoise,
												model: model || '',
											}}
											setters={{ setSteps, setBatchSize, setCfg, setDenoise }}
										/>
										<div className="flex w-full flex-col items-center justify-center gap-4 p-4 pt-0">
											<div className="flex w-full flex-row items-center justify-center gap-4">
												<div className="w-full">
													<div className="mb-2">
														<div className="flex items-center space-x-2">
															<Icon
																name="bezier-curve"
																className={`h-5 w-5 text-white`}
															/>
															<span className="text-sm font-medium text-gray-700 dark:text-accent-foreground">
																Sampler
															</span>
														</div>
													</div>
													<Select
														value={sampler}
														onValueChange={(value) =>
															setSampler(value as DBSampler)
														}
													>
														<SelectTrigger className="w-full">
															<SelectValue placeholder="Select a sampler" />
														</SelectTrigger>
														<SelectContent>
															{samplerKeys.map((key) => (
																<SelectItem key={key} value={key}>
																	{getDisplaySampler(key)}
																</SelectItem>
															))}
														</SelectContent>
													</Select>
												</div>
												<div className="w-full">
													<div className="mb-2">
														<div className="flex items-center space-x-2">
															<Icon
																name="compass-tool"
																className={`h-5 w-5 text-white`}
															/>
															<span className="text-sm font-medium text-gray-700 dark:text-accent-foreground">
																Scheduler
															</span>
														</div>
													</div>
													<Select
														value={scheduler}
														onValueChange={(value) =>
															setScheduler(value as DBScheduler)
														}
													>
														<SelectTrigger className="w-full">
															<SelectValue placeholder="Select a scheduler" />
														</SelectTrigger>
														<SelectContent>
															{schedulerKeys.map((key) => (
																<SelectItem key={key} value={key}>
																	{getDisplayScheduler(key)}
																</SelectItem>
															))}
														</SelectContent>
													</Select>
												</div>
											</div>
											<div className="flex flex-col space-y-2">
												<div className="flex items-center space-x-2">
													<Icon
														name="binoculars"
														className="h-5 w-5 text-white"
													/>
													<span className="text-sm font-medium text-gray-700 dark:text-accent-foreground">
														Minimum Face Size
													</span>
												</div>
												<div className="flex items-center space-x-2">
													<Input
														type="number"
														value={dropSize.toString()}
														onChange={(e) => {
															const newValue = e.target.value
															if (/^\d+$/.test(newValue) || newValue === '') {
																setDropSize(newValue)
															}
														}}
														onBlur={() => {
															let newValue = parseFloat(dropSize.toString())
															if (isNaN(newValue)) newValue = DROP_SIZE_MIN
															newValue = Math.min(
																DROP_SIZE_MAX,
																Math.max(DROP_SIZE_MIN, newValue),
															)
															setDropSize(Number(newValue))
														}}
														className="w-20 text-right font-mono"
														min={DROP_SIZE_MIN}
														max={DROP_SIZE_MAX}
													/>
													<span className="text-sm text-gray-500">pixels</span>
												</div>
												<p className="text-xs text-gray-500">
													Faces smaller than this size will be ignored during
													the detailing process.
												</p>
											</div>
											<div className="flex flex-col space-y-2">
												<div className="flex items-center space-x-2">
													<Icon name="sun" className="h-5 w-5 text-white" />
													<span className="text-sm font-medium text-gray-700 dark:text-accent-foreground">
														Face Detection Confidence
													</span>
												</div>
												<div className="flex items-center space-x-2">
													<Input
														type="text"
														value={
															typeof bboxThreshold === 'number'
																? (bboxThreshold * 100).toFixed(0)
																: bboxThreshold.toString()
														}
														onChange={(e) => {
															const newValue = e.target.value
															if (
																/^\d{0,3}$/.test(newValue) ||
																newValue === ''
															) {
																setBboxThreshold(Number(newValue) / 100)
															}
														}}
														onBlur={() => {
															let newValue = parseFloat(
																bboxThreshold.toString(),
															)
															if (isNaN(newValue)) newValue = 0.5
															newValue = Math.min(1, Math.max(0, newValue))
															setBboxThreshold(Number(newValue.toFixed(2)))
														}}
														className="w-[60px] text-right font-mono"
													/>
													<span className="text-sm text-gray-500">%</span>
												</div>
												<p className="text-xs text-gray-500">
													Set the minimum confidence level for face detection.
													Higher values increase precision but may miss some
													faces.
												</p>
											</div>
										</div>
									</div>
								</div>
							</ScrollArea>
							<DialogFooter className="mt-2 flex-shrink-0">
								<div className="flex flex-col items-end justify-center gap-2">
									<Button type="submit" disabled={!hasCredits}>
										<div className="flex items-center justify-center space-x-2">
											<p>Apply changes</p>
											<span className="flex items-center text-sm text-primary-foreground">
												({generationCost}
												<Icon name="coins" className="ml-1 h-4 w-4" />)
											</span>
										</div>
									</Button>

									{!hasCredits ? (
										<div className="">
											<ErrorList
												errors={[
													`Insufficient credits. You need ${generationCost} credits for this generation.`,
												]}
												id={form.errorId}
											/>
										</div>
									) : null}
								</div>
							</DialogFooter>
						</Form>
					)}
				</DialogContent>
			</Dialog>
		</>
	)
}
